import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import api from '../api';

export default function Signup() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [coachName, setCoachName] = useState('');
  const [phone, setPhone] = useState('');
  const [teamName, setTeamName] = useState('');
  const [schoolName, setSchoolName] = useState('');
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      const checkoutRes = await api.post('/billing/checkout', {
        email,
        password,
        coachName,
        phone,
        teamName,
        schoolName,
      });

      if (!checkoutRes.data?.checkoutUrl) {
        throw new Error('Could not open secure checkout.');
      }

      window.location.assign(checkoutRes.data.checkoutUrl);
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Could not create the account.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-brand-offwhite px-4 py-10">
      <div className="mx-auto grid min-h-[calc(100vh-5rem)] max-w-5xl gap-6 lg:grid-cols-[0.95fr_1.05fr]">
        <section className="rounded-lg bg-brand-black p-8 text-white shadow-[0_24px_70px_rgba(28,25,23,0.24)]">
          <img src="/mick-oc-logo.png" alt="Mick OC" className="h-auto w-44 rounded-lg" />
          <p className="mt-6 text-xs font-semibold uppercase tracking-[0.3em] text-brand-orange">Create Account</p>
          <h1 className="mt-4 text-5xl font-semibold leading-tight tracking-tight">
            Set up the primary coach account.
          </h1>
          <p className="mt-5 max-w-xl text-lg leading-8 text-stone-300">
            This starts the paid team account through secure Stripe checkout. After payment, the
            primary coach can sign in and add other coaches from the same team.
          </p>

          <div className="mt-8 rounded-lg bg-white/10 px-5 py-5">
            <p className="text-sm font-semibold uppercase tracking-[0.2em] text-brand-orange">
              Annual Price
            </p>
            <p className="mt-3 text-4xl font-semibold">$999.00 US</p>
            <p className="mt-2 text-base text-stone-200">
              per team, per year. The app creates the team account from the school and team details below.
            </p>
          </div>
        </section>

        <section className="flex items-center justify-center rounded-lg border border-stone-200 bg-white p-8 shadow-sm">
          <div className="w-full max-w-md">
            <p className="text-xs font-semibold uppercase tracking-[0.3em] text-brand-orange">
              New Account
            </p>
            <h2 className="mt-3 text-3xl font-semibold text-brand-black">Primary coach signup</h2>
            <p className="mt-3 text-base leading-7 text-stone-700">
              Enter the team details, then complete the secure payment step to activate the account.
            </p>

            <form onSubmit={handleSubmit} className="mt-8 space-y-5">
              <div>
                <label htmlFor="school-name" className="mb-2 block text-base font-medium text-stone-700">
                  School / Team
                </label>
                <input
                  id="school-name"
                  type="text"
                  value={schoolName}
                  onChange={(e) => setSchoolName(e.target.value)}
                  className="input-field text-lg"
                  placeholder="Central High School / Springfield"
                  required
                />
              </div>

              <div>
                <label htmlFor="team-name" className="mb-2 block text-base font-medium text-stone-700">
                  Team name
                </label>
                <input
                  id="team-name"
                  type="text"
                  value={teamName}
                  onChange={(e) => setTeamName(e.target.value)}
                  className="input-field text-lg"
                  placeholder="Tigers or Bears"
                  required
                />
              </div>

              <div>
                <label htmlFor="coach-name" className="mb-2 block text-base font-medium text-stone-700">
                  Primary coach name
                </label>
                <input
                  id="coach-name"
                  type="text"
                  value={coachName}
                  onChange={(e) => setCoachName(e.target.value)}
                  className="input-field text-lg"
                  placeholder="Coach Smith"
                  required
                />
              </div>

              <div>
                <label htmlFor="signup-email" className="mb-2 block text-base font-medium text-stone-700">
                  Email
                </label>
                <input
                  id="signup-email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="input-field text-lg"
                  autoComplete="email"
                  required
                />
              </div>

              <div>
                <label htmlFor="phone" className="mb-2 block text-base font-medium text-stone-700">
                  Phone
                </label>
                <input
                  id="phone"
                  type="text"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  className="input-field text-lg"
                  placeholder="555-555-5555"
                />
              </div>

              <div>
                <label htmlFor="signup-password" className="mb-2 block text-base font-medium text-stone-700">
                  Password
                </label>
                <input
                  id="signup-password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="input-field text-lg"
                  autoComplete="new-password"
                  required
                />
              </div>

              {error && (
                <p className="rounded-lg border border-rose-200 bg-rose-50 px-4 py-3 text-base text-rose-700">
                  {error}
                </p>
              )}

              <button type="submit" disabled={loading} className="btn-primary w-full justify-center text-base">
                {loading ? 'Opening secure checkout...' : 'Continue to secure payment'}
              </button>
            </form>

            <div className="mt-6 text-base text-stone-700">
              Already have one?{' '}
              <Link to="/" className="font-semibold text-brand-orange transition hover:text-brand-orange">
                Return to sign in
              </Link>
            </div>

            <div className="mt-5 flex flex-wrap gap-4 text-sm font-semibold text-stone-600">
              <Link to="/privacy" className="hover:text-brand-orange">
                Privacy
              </Link>
              <Link to="/terms" className="hover:text-brand-orange">
                Terms
              </Link>
              <Link to="/support" className="hover:text-brand-orange">
                Support
              </Link>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}
